import { useId, useRef, useState } from "react";

import { ErrorAlert, Field, Select, cx, useFormField } from "@/components/ui";
import { ApiError, errorMessage } from "@/lib/http";
import { support } from "@/lib/api";
import type { SupportFieldOption, SupportFormField } from "@/lib/types";

/** A field's current answer. Every value the vendor API accepts is a string (PHP-3773 submit contract). */
export type Answers = Record<string, string>;

const INPUT =
  "w-full rounded-[10px] border border-border bg-surface p-[13px] text-[15px] text-fg transition-colors placeholder:text-fg-subtle focus:border-brand-500 focus:outline-none";

/**
 * Whether `field` should be shown given the current `answers`. The vendor's `visibleWhen` shape isn't
 * documented beyond "conditional visibility" (portal-api-documentation.pdf), so this only recognises the
 * common `{ fieldId, equals }` / `{ fieldId, oneOf }` shapes and otherwise shows the field — hiding a
 * field this portal can't actually evaluate could silently drop a required answer, which the vendor's own
 * integration guide says never to do.
 */
export function isFieldVisible(field: SupportFormField, answers: Answers): boolean {
  const rule = field.visible_when as {
    fieldId?: string;
    field_id?: string;
    equals?: string;
    oneOf?: string[];
    one_of?: string[];
  } | null;
  if (!rule) return true;
  const targetId = rule.fieldId ?? rule.field_id;
  if (!targetId) return true;
  const value = answers[targetId] ?? "";
  const oneOf = rule.oneOf ?? rule.one_of;
  if (Array.isArray(oneOf)) return oneOf.includes(value);
  if (typeof rule.equals === "string") return value === rule.equals;
  return true;
}

/** `Parent option > Child option` (PHP-3773's nested-select encoding) when a child was chosen, else the parent alone. */
function encodeSelect(parentValue: string, childValue: string): string {
  return childValue ? `${parentValue} > ${childValue}` : parentValue;
}

function TextInput({
  field,
  value,
  onChange,
  type = "text",
}: {
  field: SupportFormField;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "email" | "number";
}) {
  return (
    <Field
      label={field.label}
      name={field.id}
      required={field.is_required}
      hint={field.helper_text}
      size="lg"
    >
      {(control) => (
        <input
          {...control}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder ?? undefined}
          maxLength={typeof field.validation.maxLength === "number" ? field.validation.maxLength : undefined}
          min={typeof field.validation.min === "number" ? field.validation.min : undefined}
          max={typeof field.validation.max === "number" ? field.validation.max : undefined}
          className={INPUT}
        />
      )}
    </Field>
  );
}

function RichTextInput({
  field,
  value,
  onChange,
}: {
  field: SupportFormField;
  value: string;
  onChange: (value: string) => void;
}) {
  // No rich-text editor exists yet in this portal (frontend survey, PHP-3773): a plain multi-line field
  // collects the same text until one is built; the vendor accepts a plain string either way.
  return (
    <Field
      label={field.label}
      name={field.id}
      required={field.is_required}
      hint={field.helper_text}
      size="lg"
    >
      {(control) => (
        <textarea
          {...control}
          rows={5}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder ?? undefined}
          className={cx(INPUT, "resize-y")}
        />
      )}
    </Field>
  );
}

function SelectInput({
  field,
  value,
  onChange,
}: {
  field: SupportFormField;
  value: string;
  onChange: (value: string) => void;
}) {
  const [parent, child] = (() => {
    const [p, c] = value.split(" > ");
    return [p ?? "", c ?? ""];
  })();
  const parentOption = field.options.find((o) => o.value === parent);
  const childOptions: SupportFieldOption[] = parentOption?.child_options ?? [];
  return (
    <Field
      label={field.label}
      name={field.id}
      required={field.is_required}
      hint={field.helper_text}
      size="lg"
    >
      {(control) => (
        <div className="flex flex-col gap-2 sm:flex-row">
          <Select
            {...control}
            value={parent}
            onChange={(e) => onChange(encodeSelect(e.target.value, ""))}
            className="flex-1"
          >
            <option value="">Choose one</option>
            {field.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          {childOptions.length > 0 && (
            <Select
              aria-label={`${field.label}: sub-option`}
              value={child}
              onChange={(e) => onChange(encodeSelect(parent, e.target.value))}
              className="flex-1"
            >
              <option value="">Choose one</option>
              {childOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          )}
        </div>
      )}
    </Field>
  );
}

function RadioInput({
  field,
  value,
  onChange,
}: {
  field: SupportFormField;
  value: string;
  onChange: (value: string) => void;
}) {
  const groupId = useId();
  const { message, messageId } = useFormField({
    label: field.label,
    name: field.id,
    required: field.is_required,
  });
  return (
    <div className="flex flex-col gap-1.5">
      <p id={groupId} className="text-[13px] font-semibold text-fg-muted">
        {field.label}
        {field.is_required && <span className="ml-0.5 text-red-600">*</span>}
      </p>
      <div
        role="radiogroup"
        aria-labelledby={groupId}
        aria-describedby={messageId}
        className="flex flex-col gap-2"
      >
        {field.options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm text-fg">
            <input
              type="radio"
              name={field.id}
              required={field.is_required}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
              className="accent-brand-500"
            />
            {o.label}
          </label>
        ))}
      </div>
      {message && (
        <p id={messageId} className="text-xs text-red-600">
          {message}
        </p>
      )}
      {field.helper_text && !message && <p className="text-xs text-fg-subtle">{field.helper_text}</p>}
    </div>
  );
}

/** Selected options, comma-separated; a selected optionless checkbox is `"true"` (empty/unselected: `""`). */
function CheckboxInput({
  field,
  value,
  onChange,
}: {
  field: SupportFormField;
  value: string;
  onChange: (value: string) => void;
}) {
  const groupId = useId();
  if (field.options.length === 0) {
    const checked = value === "true";
    return (
      <label className="flex items-center gap-2 text-sm text-fg">
        <input
          type="checkbox"
          name={field.id}
          required={field.is_required}
          checked={checked}
          onChange={(e) => onChange(e.target.checked ? "true" : "")}
          className="accent-brand-500"
        />
        {field.label}
        {field.is_required && <span className="ml-0.5 text-red-600">*</span>}
      </label>
    );
  }
  const selected = new Set(value ? value.split(",") : []);
  return (
    <div className="flex flex-col gap-1.5">
      <p id={groupId} className="text-[13px] font-semibold text-fg-muted">
        {field.label}
        {field.is_required && <span className="ml-0.5 text-red-600">*</span>}
      </p>
      <div role="group" aria-labelledby={groupId} className="flex flex-col gap-2">
        {field.options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={selected.has(o.value)}
              onChange={(e) => {
                const next = new Set(selected);
                if (e.target.checked) next.add(o.value);
                else next.delete(o.value);
                onChange(Array.from(next).join(","));
              }}
              className="accent-brand-500"
            />
            {o.label}
          </label>
        ))}
      </div>
      {field.helper_text && <p className="text-xs text-fg-subtle">{field.helper_text}</p>}
    </div>
  );
}

function DateInput({
  field,
  value,
  onChange,
}: {
  field: SupportFormField;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Field
      label={field.label}
      name={field.id}
      required={field.is_required}
      hint={field.helper_text}
      size="lg"
    >
      {(control) => (
        <input
          {...control}
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={INPUT}
        />
      )}
    </Field>
  );
}

type UploadState = {
  status: "idle" | "uploading" | "done" | "error";
  progress: number;
  fileName?: string;
  error?: unknown;
};

function FileInput({
  field,
  value,
  onChange,
}: {
  field: SupportFormField;
  value: string;
  onChange: (value: string) => void;
}) {
  const [state, setState] = useState<UploadState>({ status: value ? "done" : "idle", progress: 0 });
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = async (file: File) => {
    setState({ status: "uploading", progress: 0, fileName: file.name });
    try {
      const result = await support.uploadFile(field.id, file, (sent, total) =>
        setState((s) => ({ ...s, progress: total ? Math.round((sent / total) * 100) : s.progress })),
      );
      onChange(result.url);
      setState({ status: "done", progress: 100, fileName: result.file_name });
    } catch (error) {
      setState({ status: "error", progress: 0, fileName: file.name, error });
    }
  };

  return (
    <Field
      label={field.label}
      name={field.id}
      required={field.is_required}
      hint={field.helper_text}
      size="lg"
    >
      {(control) => (
        <div className="flex flex-col gap-2">
          {state.status !== "done" && (
            <input
              {...control}
              ref={inputRef}
              type="file"
              required={field.is_required && state.status === "idle"}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void upload(file);
              }}
              className="text-sm text-fg-muted file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-fg file:transition-colors hover:file:bg-border"
            />
          )}
          {state.status === "uploading" && (
            <p className="text-xs text-fg-subtle">
              Uploading {state.fileName}… {state.progress}%
            </p>
          )}
          {state.status === "done" && (
            <div className="flex items-center justify-between rounded-lg border border-border bg-muted px-3 py-2 text-sm">
              <span className="truncate text-fg">{state.fileName ?? "File attached"}</span>
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  setState({ status: "idle", progress: 0 });
                  if (inputRef.current) inputRef.current.value = "";
                }}
                className="text-xs font-semibold text-brand-500 hover:underline"
              >
                Remove
              </button>
            </div>
          )}
          {state.status === "error" && (
            <ErrorAlert
              error={state.error instanceof ApiError ? state.error : new Error(errorMessage(state.error))}
            />
          )}
        </div>
      )}
    </Field>
  );
}

function UnsupportedField({ field }: { field: SupportFormField }) {
  return (
    <div
      role="alert"
      className="rounded-lg border border-[#ffc4bb] bg-[#fff1ee] px-3 py-2.5 text-sm text-danger-fg"
    >
      “{field.label}” needs a type of form field this page doesn’t support yet (<code>{field.type}</code>).
      Please contact support another way for this request, or leave this field blank if it isn’t required.
    </div>
  );
}

/** One configured field, dispatched to its control by `field.type` (PHP-3773: text/richtext/email/number/
 * select/radio/checkbox/date/file). An unknown type gets a visible, non-blocking notice instead of being
 * silently dropped. */
export function FieldRenderer({
  field,
  value,
  onChange,
}: {
  field: SupportFormField;
  value: string;
  onChange: (value: string) => void;
}) {
  switch (field.type) {
    case "text":
      return <TextInput field={field} value={value} onChange={onChange} />;
    case "email":
      return <TextInput field={field} value={value} onChange={onChange} type="email" />;
    case "number":
      return <TextInput field={field} value={value} onChange={onChange} type="number" />;
    case "richtext":
      return <RichTextInput field={field} value={value} onChange={onChange} />;
    case "select":
      return <SelectInput field={field} value={value} onChange={onChange} />;
    case "radio":
      return <RadioInput field={field} value={value} onChange={onChange} />;
    case "checkbox":
      return <CheckboxInput field={field} value={value} onChange={onChange} />;
    case "date":
      return <DateInput field={field} value={value} onChange={onChange} />;
    case "file":
      return <FileInput field={field} value={value} onChange={onChange} />;
    default:
      return <UnsupportedField field={field} />;
  }
}
